import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sun, Moon } from '@phosphor-icons/react';
import { TFMark } from './TFLogo';
import usePublicTheme from '../hooks/usePublicTheme';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_FLOW = {
  initiated: { label: 'Initiated', icon: '1', color: '#C49A23' },
  delivery_confirmed: { label: 'Delivery Confirmed', icon: '2', color: '#3B82F6' },
  funds_released: { label: 'Funds Released', icon: '3', color: '#2D7A3A' },
  disputed: { label: 'Disputed', icon: '!', color: '#C43A23' },
  resolved: { label: 'Resolved', icon: '4', color: '#2D7A3A' },
};

export default function VerifyPage({ certId }) {
  const [mode, toggleTheme] = usePublicTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCert = async () => {
      try {
        const res = await fetch(`${API}/api/verify/${certId}`);
        if (!res.ok) { setError((await res.json()).detail || 'Certificate not found'); }
        else { setData(await res.json()); }
      } catch (err) { setError('Unable to verify certificate.'); }
      finally { setLoading(false); }
    };
    fetchCert();
  }, [certId]);

  const downloadPdf = async () => {
    try {
      const res = await fetch(`${API}/api/verify/${data.txn_id || certId}/pdf`);
      if (!res.ok) return;
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `TrustFrame_Certificate_${data.txn_id || certId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {}
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border border-tf-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="font-mono text-tf-text-2 text-[10px] tracking-[0.3em] uppercase">Verifying Certificate</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen" data-testid="verify-error-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
        <Header mode={mode} toggleTheme={toggleTheme} />
        <div className="bg-tf-surface border border-tf-danger p-6 sm:p-8 text-center">
          <div className="w-12 h-12 border-2 border-tf-danger rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-tf-danger font-heading font-bold text-xl">!</span>
          </div>
          <h2 className="font-heading font-bold text-lg sm:text-xl tracking-[0.1em] uppercase text-tf-text mb-2">Certificate Not Found</h2>
          <p className="font-body text-sm text-tf-text-2 break-words">{error}</p>
          <p className="font-mono text-[10px] text-tf-text-3 mt-4 break-all">Contact verify@trustframe.legal</p>
        </div>
      </div>
    </div>
  );

  const txn = data.transaction || {};
  const owner = data.owner || {};
  const history = txn.status_history || [];

  return (
    <div className="min-h-screen" data-testid="verify-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Header mode={mode} toggleTheme={toggleTheme} />

        {/* Verification Badge */}
        <div className="bg-tf-surface border border-tf-success p-5 sm:p-6 flex items-center gap-4" data-testid="verify-badge">
          <div className="w-12 h-12 sm:w-14 sm:h-14 bg-tf-success rounded-full flex items-center justify-center flex-shrink-0">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
          </div>
          <div className="min-w-0">
            <h2 className="font-heading font-bold text-base sm:text-lg tracking-[0.15em] uppercase text-tf-success">Verified Certificate</h2>
            <p className="font-mono text-[10px] text-tf-text-2 mt-0.5 break-words">Authenticated and sealed by TrustFrame</p>
          </div>
        </div>

        {/* Certificate Details */}
        <Section title="Certificate of Title" data-testid="cert-details" dark>
          <Row label="Certificate ID" value={data.cert_id} mono />
          <Row label="Transaction ID" value={data.txn_id} mono />
          <Row label="Issued" value={formatDate(data.issued_at)} />
        </Section>

        {txn.description && (
          <Section title="Artwork Details" data-testid="artwork-details">
            <Row label="Description" value={txn.description} />
            <Row label="Value" value={`£${Number(txn.amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`} accent />
            <Row label="Tier" value={`Tier ${txn.tier}`} />
            <Row label="Fee" value={`${txn.fee_pct}%`} />
            <Row label="Status" value={
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 border font-mono text-[9px] tracking-[0.15em] uppercase ${
                txn.status === 'funds_released' ? 'border-tf-success text-tf-success' : 'border-tf-primary text-tf-primary'
              }`}>
                <div className={`w-1.5 h-1.5 rounded-full ${txn.status === 'funds_released' ? 'bg-tf-success' : 'bg-tf-primary'}`} />
                {(txn.status || '').replace(/_/g, ' ')}
              </span>
            } />
          </Section>
        )}

        {owner.name && (
          <Section title="Verified Owner" data-testid="owner-details">
            <Row label="Name" value={owner.name} bold />
            <Row label="Organisation" value={owner.org} />
            <Row label="Role" value={owner.role} />
            <Row label="Country" value={owner.country} />
            <Row label="TrustFrame ID" value={owner.trustframe_id} mono />
            <Row label="KYC" value={
              <span className={`flex items-center gap-1.5 ${owner.kyc_status === 'verified' ? 'text-tf-success' : 'text-tf-warning'}`}>
                <div className={`w-2 h-2 rounded-full ${owner.kyc_status === 'verified' ? 'bg-tf-success' : 'bg-tf-warning'}`} />
                <span className="font-mono text-xs capitalize font-bold">{owner.kyc_status || 'pending'}</span>
              </span>
            } />
          </Section>
        )}

        {history.length > 0 && (
          <Section title="Transaction Timeline" data-testid="txn-timeline">
            <div className="space-y-0">
              {history.map((h, i) => {
                const cfg = STATUS_FLOW[h.status] || { label: h.status, icon: '?', color: '#999' };
                const isLast = i === history.length - 1;
                return (
                  <div key={i} className="flex gap-4" data-testid={`timeline-step-${i}`}>
                    <div className="flex flex-col items-center">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-white font-heading font-bold text-xs flex-shrink-0" style={{ backgroundColor: cfg.color }}>{cfg.icon}</div>
                      {!isLast && <div className="w-px h-8 bg-tf-border-light" />}
                    </div>
                    <div className={isLast ? '' : 'pb-4'}>
                      <p className="font-heading font-bold text-sm text-tf-text">{cfg.label}</p>
                      <p className="font-mono text-[10px] text-tf-text-2">{formatDate(h.at)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {data.provenance_hash && (
          <Section title="Provenance Hash" data-testid="provenance-hash">
            <p className="font-mono text-[10px] text-tf-text-2 break-all">{data.provenance_hash}</p>
            <p className="font-mono text-[9px] text-tf-text-3 mt-2 italic">Immutable fingerprint of the transaction record.</p>
          </Section>
        )}

        <button data-testid="download-cert-pdf" onClick={downloadPdf}
          className="w-full mt-6 min-h-[48px] py-3 px-4 bg-tf-accent text-white font-heading font-bold text-xs tracking-[0.3em] uppercase hover:bg-tf-accent-hover transition-all duration-300">
          Download Certificate PDF
        </button>

        <div className="mt-8 pt-6 border-t border-tf-border-light text-center">
          <p className="font-mono text-[10px] text-tf-text-2 tracking-[0.15em] break-words">Verified by <span className="font-bold text-tf-text">TrustFrame</span></p>
          <p className="font-mono text-[9px] text-tf-text-3 mt-1 break-words">[ .secure. .art. .transactions. .built. .on. .trust. ]</p>
        </div>
      </div>
    </div>
  );
}

function Header({ mode, toggleTheme }) {
  return (
    <header className="border-b border-tf-border-muted px-4 sm:px-8 py-3 sm:py-4 -mx-4 sm:-mx-6 mb-6 sm:mb-8">
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-2 sm:gap-3">
        <Link to="/" className="flex items-center gap-3 sm:gap-6 min-h-[44px] min-w-0" data-testid="verify-home-link">
          <TFMark color="currentColor" size={24} />
          <span className="font-machina font-bold text-[9px] xs:text-[10px] sm:text-xs uppercase whitespace-nowrap" style={{ letterSpacing: '0.55em', paddingLeft: '0.55em' }}>TRUSTFRAME</span>
        </Link>
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={toggleTheme}
            data-testid="verify-theme-toggle"
            aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            className="min-h-[36px] min-w-[36px] px-2 inline-flex items-center justify-center border border-tf-border-muted text-tf-text-2 hover:text-tf-text transition-colors"
          >
            {mode === 'dark' ? <Sun size={14} weight="bold" /> : <Moon size={14} weight="bold" />}
          </button>
          <Link to="/" className="min-h-[44px] px-2 font-mono text-[9px] sm:text-[10px] tracking-[0.2em] uppercase text-tf-text-2 hover:text-tf-text flex items-center gap-1.5 sm:gap-2">
            <ArrowLeft size={12} weight="bold" /> <span className="hidden xs:inline">Back</span>
          </Link>
        </div>
      </div>
      <div className="max-w-4xl mx-auto mt-3 sm:mt-4">
        <p className="font-mono text-[9px] sm:text-[10px] tracking-[0.25em] uppercase text-tf-text-2 break-words">[ .certificate. .verification. ]</p>
      </div>
    </header>
  );
}

function Section({ title, children, dark, ...rest }) {
  return (
    <div className="bg-tf-surface border border-tf-primary mt-6" {...rest}>
      <div className={`px-5 py-3 border-b ${dark ? 'bg-tf-primary border-tf-primary' : 'bg-tf-bg border-tf-border-muted'}`}>
        <h3 className={`font-heading font-bold text-xs tracking-[0.2em] uppercase ${dark ? 'text-white' : 'text-tf-text-2'}`}>{title}</h3>
      </div>
      <div className="p-5 space-y-3">{children}</div>
    </div>
  );
}

function Row({ label, value, mono, bold, accent }) {
  if (!value) return null;
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4">
      <span className="font-mono text-[10px] text-tf-text-2 sm:w-28 flex-shrink-0 sm:pt-0.5 tracking-[0.1em] uppercase">{label}</span>
      <span className={`font-body text-sm text-tf-text break-all min-w-0 ${mono ? 'font-mono text-xs' : ''} ${bold ? 'font-semibold' : ''} ${accent ? 'font-heading font-bold text-tf-accent break-words' : ''}`}>{value}</span>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
